import { call, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { accountsActions } from '../accounts';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { featureFlagsSelectors } from '../feature-flags';
import { transactionsActions } from '../transactions';

import { getEnvironmentConfig, getLogger, httpClientInterface } from '../../utils/core';
import * as actions from './goalsActions';
import { getLastSyncDate } from './goalsSelectors';
import { transformGoalToRequestData, transformQcsGoalsToGoals, transformResponseDataToGoal } from './goalsTransformers';

const log = getLogger('flux/goals/goalsSagas');

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('goalsDumpUpdater');
export function* goalsDumpUpdater(resources) {
  const bumpGoals = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpGoals');
  if (bumpGoals) {
    yield callFirst(goalsDumpUpdaterFull, resources);
    yield callLast(goalsNudgeUpdater);
  }
}

function* goalsDumpUpdaterFull(resources) {
  yield put(actions.updateChangedGoals(transformQcsGoalsToGoals(resources), { context: 'BUMP_CHANGE_DUMP' }));
}

export function* goalsNudgeUpdater() {
  const bumpGoals = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpGoals');
  if (bumpGoals) {
    yield put(actions.getGoals(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

const mkResourceConfig = (successAction, failureAction) =>
  resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceName: 'goal',
    resourceBaseUrl: '/goals',
    getLastSyncDate,
    applyResourceFromCRUDResponse: true,

    transformServerResourceToClientResource: transformResponseDataToGoal,
    transformClientResourceToRequestData: transformGoalToRequestData,

    successAction,
    failureAction,
  });

export function* getGoalsActionWatcher() {
  const config = mkResourceConfig(actions.getGoalsSuccess, actions.getGoalsFailure);
  yield resourceSagaUtils.takeAndDebounce(actions.getGoals, resourceSagaUtils.qcsSyncGetResources, config);
}

export function* updateGoalActionWatcher() {
  const config = mkResourceConfig(actions.updateGoalSuccess, actions.updateGoalFailure);
  yield takeEvery(actions.updateGoal, resourceSagaUtils.qcsSyncUpdateResource, config);
}

export function* deleteGoalSuccessActionWatcher() {
  yield takeEvery(actions.deleteGoalSuccess, function* deleteGoalSuccess(action) {
    yield put(
      accountsActions.applyAccountsChanges(
        [
          {
            // local optimistic cascade account deletion
            id: action.payload.accountId,
            isDeleted: true,
          },
        ],
        { context: 'deleteGoalSuccess' },
      ),
    );
  });
}

export function* deleteGoalActionWatcher() {
  const config = mkResourceConfig(actions.deleteGoalSuccess, actions.deleteGoalFailure);
  yield takeEvery(actions.deleteGoal, resourceSagaUtils.qcsSyncDeleteResource, config);
}

// eventually QCS will take the responsibility of creating the tag and the transactions, then this will need to be replaced with the default action
export function* performCreateGoalWatcher() {
  yield takeEvery(actions.performCreateGoal, function* performCreateGoal({ payload, meta }) {
    const { goal, accountId } = payload;
    const { resolve, reject, context } = meta;

    // if we want optimistic update, but I'm not convinced we do with Goals:
    // yield put(actions.createGoal(goal));

    try {
      const requestUrl = `${getEnvironmentConfig().services_url}/goals`;
      const requestPayload = {
        ...transformGoalToRequestData(goal),
        initialContributionAmount: goal.savedSoFar || undefined,
        initialContributionAccountId: accountId,
      };

      const response = yield call(httpClientInterface.send, 'POST', requestUrl, requestPayload);

      yield put(actions.createGoalSuccess(transformResponseDataToGoal(response.data.resource), { context }));
      yield call(resolve);
    } catch (error) {
      log.log('Error creating goal, error=', error);

      yield put(actions.createGoalFailure({ payload: error }));
      yield call(reject);
    }
  });
}

function* refreshGoalsTrigger() {
  while (true) {
    const { createTransactionSuccess, batchTransactionsSuccess } = yield race({
      createTransactionSuccess: take(transactionsActions.createTransactionSuccess),
      batchTransactionsSuccess: take(transactionsActions.batchTransactionsSuccess),
    });

    if (
      (createTransactionSuccess || batchTransactionsSuccess)?.meta?.context === 'WithdrawFromGoal' ||
      (createTransactionSuccess || batchTransactionsSuccess)?.meta?.context === 'ContributeToGoal' ||
      (createTransactionSuccess || batchTransactionsSuccess)?.meta?.context === 'spending-plan-goals' ||
      (createTransactionSuccess || batchTransactionsSuccess)?.meta?.context === 'Goals' ||
      (createTransactionSuccess || batchTransactionsSuccess)?.meta?.context === 'AddGoal'
    ) {
      yield put(actions.getGoals(null, { ...(createTransactionSuccess || batchTransactionsSuccess)?.meta }));
    }
  }
}

// ====================================================
// EXPORTS

export default [
  getGoalsActionWatcher,
  updateGoalActionWatcher,
  deleteGoalActionWatcher,
  deleteGoalSuccessActionWatcher,
  refreshGoalsTrigger,
  performCreateGoalWatcher,
];
